import React from 'react';
import {useSelectorWithRefresh} from '@/store/kysely_module';
import {useCurrentUser} from '../hooks/useCurrentUser';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {
    Card,
    Container,
    Group,
    Stack,
    Text,
    Title,
    Badge,
    Box,
    Table,
    Flex,
} from '@mantine/core';
import {IconMusic} from '@tabler/icons-react';
import {PlayIcon} from '@/modules/audio_player/audio_player_module';
import {formatDuration, formatTimeAgo} from '@/components/new_dashboard/utils/timeFormatting';
import {isAudioFile} from '@/utils/file_utils';
import {navigateToPathForReason} from '@/modules/navigation/navigation_module';
import {useIsMobile} from '@/utils/user_agent';
import {ProfileSidebar} from '../components/profile_sidebar/ProfileSidebar';
import {EditProfileModal} from '../components/modals/edit_profile_modal';
import {ProfileMobileView} from '../components/profile_mobile/ProfileMobileView';
import {LoadingIndicator} from '@/components/common/LoadingIndicator';

type UserProfileContentProps = {
    userId: string;
};

export const UserProfileContent = (props: UserProfileContentProps) => {
    const currentUser = useCurrentUser();
    const navigate = useNavigate();
    const isOwnProfile = currentUser?.id === props.userId;
    const isEditMode = isOwnProfile;

    const [publicProfileData, refreshPublicProfile, isLoadingPublicProfile] = useSelectorWithRefresh('getUserProfileData', {user_id: props.userId}, null, {workspace_id: 'public'});
    const [authenticatedData, refreshAuthenticatedData] = useSelectorWithRefresh('getUserProfileDataAuthenticated', {user_id: props.userId}, null, {workspace_id: 'public'});

    const handleProfileUpdate = () => {
        refreshPublicProfile();
        refreshAuthenticatedData();
    };

    if (isLoadingPublicProfile) {
        return <LoadingIndicator />;
    }

    if (!publicProfileData?.user) {
        return (
            <Container size="md" py="xl">
                <Text>User not found</Text>
            </Container>
        );
    }

    const {user, publicFiles, bio, socialLinks} = publicProfileData;

    return (
        <Flex style={{minHeight: '100vh'}}>
            <Box
                style={{
                    width: 350,
                    position: 'sticky',
                    top: 0,
                    height: '100vh',
                    overflowY: 'auto',
                    borderRight: '1px solid var(--mantine-color-default-border)',
                    backgroundColor: 'var(--mantine-color-body)',
                }}
            >
                <ProfileSidebar
                    userId={user.id}
                    userName={user.name}
                    userHandle={user.name.toLowerCase().replace(/\s+/g, '_')}
                    profilePictureFileId={user.profile_picture_file_id}
                    updatedAt={user.updated_at}
                    bio={bio || undefined}
                    socialLinks={socialLinks || undefined}
                    sharedTeams={authenticatedData?.mutualWorkspaces || undefined}
                    isEditMode={isEditMode}
                    onUpdate={handleProfileUpdate}
                />
            </Box>

            <Box style={{flex: 1, overflowY: 'auto'}}>
                <Container size="lg" py="xl">
                    <Stack gap="xl">
                        <Box>
                            <Title order={3} mb="md">
                                Featured Tracks
                            </Title>
                            {publicFiles.length === 0 ? (
                                <Card withBorder p="xl" radius="md">
                                    <Stack align="center" gap="md">
                                        <IconMusic size={48} stroke={1.5} style={{opacity: 0.5}} />
                                        <Text size="lg" c="dimmed">
                                            {isOwnProfile ? "You haven't shared any tracks publicly yet" : `${user.name} hasn't shared any tracks publicly yet`}
                                        </Text>
                                        {isOwnProfile && (
                                            <Text size="sm" c="dimmed">
                                                Upload files and mark them as public to showcase your work!
                                            </Text>
                                        )}
                                    </Stack>
                                </Card>
                            ) : (
                                <Card withBorder p="md" radius="md">
                                    <Table highlightOnHover>
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th style={{width: 50}}>#</Table.Th>
                                                <Table.Th>Name</Table.Th>
                                                <Table.Th>Composer</Table.Th>
                                                <Table.Th>Time</Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {publicFiles.map((file, index) => {
                                                const duration = file.attributes?.duration;
                                                return (
                                                    <Table.Tr key={file.id} style={{cursor: 'pointer'}}>
                                                        <Table.Td>
                                                            <Text size="sm" c="dimmed">
                                                                {String(index + 1).padStart(2, '0')}
                                                            </Text>
                                                        </Table.Td>
                                                        <Table.Td>
                                                            <Group gap="xs">
                                                                {isAudioFile(file.name) && <PlayIcon fileInfo={file} size={16} />}
                                                                <Text size="sm">{file.name}</Text>
                                                            </Group>
                                                        </Table.Td>
                                                        <Table.Td>
                                                            <Text size="sm">
                                                                {file.is_own_track ? user.name : file.creator_name}
                                                            </Text>
                                                        </Table.Td>
                                                        <Table.Td>
                                                            {duration && (
                                                                <Text size="sm" c="dimmed">
                                                                    {formatDuration(Number(duration))}
                                                                </Text>
                                                            )}
                                                        </Table.Td>
                                                    </Table.Tr>
                                                );
                                            })}
                                        </Table.Tbody>
                                    </Table>
                                </Card>
                            )}
                        </Box>

                        {authenticatedData && authenticatedData.recentProjectRevisions && authenticatedData.recentProjectRevisions.length > 0 && (
                            <Box>
                                <Title order={3} mb="md">
                                    Recent Activity
                                </Title>
                                <Text size="sm" c="dimmed" mb="md">
                                    Activity from workspaces you both belong to
                                </Text>

                                <Card withBorder p="md" radius="md">
                                    <Table highlightOnHover>
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>Project</Table.Th>
                                                <Table.Th>Workspace</Table.Th>
                                                <Table.Th>Updated</Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {authenticatedData.recentProjectRevisions.map((revision) => {
                                                const workspace = authenticatedData.mutualWorkspaces.find(w => w.id === revision.workspace_id);
                                                return (
                                                    <Table.Tr
                                                        key={revision.id}
                                                        style={{cursor: 'pointer'}}
                                                        onClick={() => {
                                                            navigateToPathForReason(
                                                                navigate,
                                                                'navigate_to_project_page_from_project_view_page',
                                                                `/workspaces/${revision.workspace_id}/projects/${revision.project_id}`
                                                            );
                                                        }}
                                                    >
                                                        <Table.Td>
                                                            <Text size="sm">{revision.project_name}</Text>
                                                        </Table.Td>
                                                        <Table.Td>
                                                            <Badge size="sm" variant="light">
                                                                {workspace?.name || 'Unknown'}
                                                            </Badge>
                                                        </Table.Td>
                                                        <Table.Td>
                                                            <Text size="sm" c="dimmed">
                                                                {formatTimeAgo(revision.created_at)}
                                                            </Text>
                                                        </Table.Td>
                                                    </Table.Tr>
                                                );
                                            })}
                                        </Table.Tbody>
                                    </Table>
                                </Card>
                            </Box>
                        )}
                    </Stack>
                </Container>
            </Box>
        </Flex>
    );
};

type Props = {
    userId: string;
};

export const UserProfilePage = (props: Props) => {
    const isMobile = useIsMobile();
    const currentUser = useCurrentUser();
    const [searchParams, setSearchParams] = useSearchParams();
    const isEditMode = searchParams.get('edit') === 'true';
    const isOwnProfile = currentUser?.id === props.userId;

    const handleCloseEdit = () => {
        searchParams.delete('edit');
        setSearchParams(searchParams);
    };

    if (isMobile) {
        return <ProfileMobileView userId={props.userId} />;
    }

    if (isEditMode && isOwnProfile) {
        return <EditProfileModal data={{}} close={handleCloseEdit} />;
    }

    return <UserProfileContent userId={props.userId} />;
};